import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import * as XLSX from "xlsx";

const formatDate = (date) => {
  if (!date) return "Tarih yok";
  if (date.seconds) {
    return new Date(date.seconds * 1000).toLocaleDateString("tr-TR");
  }
  try {
    return new Date(date).toLocaleDateString("tr-TR");
  } catch {
    return "Geçersiz Tarih";
  }
};

const calculateDaysAgo = (date) => {
  if (!date?.seconds) return "";
  const now = new Date();
  const created = new Date(date.seconds * 1000);
  const diffTime = Math.abs(now - created);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return `(${diffDays} gün)`;
};

// TR uyumlu slug
const toSlugTR = (str = "") => {
  const map = { 'Ç':'c','Ö':'o','Ş':'s','İ':'i','I':'i','Ğ':'g','Ü':'u','ç':'c','ö':'o','ş':'s','ı':'i','ğ':'g','ü':'u' };
  const replaced = (str || "").replace(/[ÇÖŞİIĞÜçöşığü]/g, ch => map[ch] || ch);
  return replaced
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/* ====== Küçük boy filtre stili ====== */
const fieldStyle = {
  width: "100%",
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #cbd5e1",
  fontSize: 13,
  boxSizing: "border-box",
};
const chipStyle = {
  display: "inline-block",
  background: "#f1f5f9",
  border: "1px solid #e2e8f0",
  borderRadius: 999,
  padding: "2px 10px",
  fontSize: 12,
  color: "#0f172a",
};
const targetTypeOptions = [
  { value: "vehicle", label: "Arac" },
  { value: "site", label: "Santiye" },
  { value: "facility", label: "Tesis" },
];
const targetTypeFilterStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
  minHeight: 34,
  padding: "5px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  background: "#fff",
  boxSizing: "border-box",
};
const targetTypeOptionStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: 13,
  color: "#0f172a",
  whiteSpace: "nowrap",
};
const photoCellStyle = {
  border: "1px solid #ccc",
  padding: "8px",
  verticalAlign: "top",
  width: 150,
  maxWidth: 150,
};
const photoWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  maxWidth: 138,
};
const photoLinkStyle = {
  width: 42,
  height: 42,
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  overflow: "hidden",
  background: "#f8fafc",
  color: "#0f172a",
  textDecoration: "none",
  fontSize: 11,
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
};
const photoImgStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

const getTargetType = (item) => item?.targetType || "vehicle";
const getTargetLabel = (item) => {
  if (getTargetType(item) === "site") return item.siteName || item.matchedSiteName || "Santiye";
  if (getTargetType(item) === "facility") return item.facilityName || item.tesis || "Tesis";
  return item.plate || "-";
};
const getTargetKindLabel = (item) => {
  if (getTargetType(item) === "site") return "Santiye";
  if (getTargetType(item) === "facility") return "Tesis";
  return "Arac";
};
const getTargetKey = (item) => `${getTargetType(item)}|${getTargetLabel(item).toLocaleUpperCase("tr-TR")}`;
const getPhotoUrls = (item) => Array.isArray(item?.photoUrls) ? item.photoUrls.filter(Boolean) : [];
const escapeHtml = (value = "") => String(value)
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");
const downloadHtmlExcel = (html, filename) => {
  const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/* ================== Plaka Autocomplete ================== */
const AutocompletePlate = ({ value, onCommit, options, placeholder = "Tüm Plakalar" }) => {
  const [input, setInput] = useState(value || "");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => { setInput(value || ""); }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        onCommit((input || "").trim());
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [input, onCommit]);

  const filtered = useMemo(() => {
    const s = (input || "").toLowerCase();
    if (!s) return options.slice(0, 50);
    return options.filter(p => (p || "").toLowerCase().includes(s)).slice(0, 50);
  }, [input, options]);

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      onCommit((input || "").trim());
      setOpen(false);
    }
  };

  return (
    <div style={{ position: "relative" }} ref={wrapRef}>
      <input
        value={input}
        onChange={(e) => { setInput(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        style={fieldStyle}
      />
      {open && filtered.length > 0 && (
        <ul style={{
          position: "absolute", zIndex: 50, listStyle: "none", margin: "4px 0 0 0", padding: 0,
          width: "100%", maxHeight: 220, overflowY: "auto", background: "#fff",
          border: "1px solid #cbd5e1", borderRadius: 6, boxShadow: "0 6px 16px rgba(0,0,0,0.08)"
        }}>
          {filtered.map((opt, i) => (
            <li
              key={`${opt}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); onCommit(opt); setInput(opt); setOpen(false); }}
              style={{ padding: "8px 10px", cursor: "pointer" }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
/* ======================================================= */

const ViewIssues = () => {
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [selectedTesis, setSelectedTesis] = useState("");
  const [selectedPlate, setSelectedPlate] = useState("");
  const [selectedTargetTypes, setSelectedTargetTypes] = useState(targetTypeOptions.map((option) => option.value));
  const toggleTargetType = (targetType) => {
    setSelectedTargetTypes((current) => (
      current.includes(targetType)
        ? current.filter((item) => item !== targetType)
        : [...current, targetType]
    ));
  };
  const [terminMap, setTerminMap] = useState({});   // termin kategorileri
  const [titleMap, setTitleMap] = useState({});     // başlık→kategori (id=slug)
  const [terminFilter, setTerminFilter] = useState("all"); // all | not_overdue | overdue
  const [lastResolvedIdx, setLastResolvedIdx] = useState({}); // 🔥 PLATE|slug(issue) → last resolvedAt (seconds)

  // Açık (çözülmemiş) issues
  useEffect(() => {
    const qRef = query(collection(db, "issues"), where("resolved", "==", false));
    const unsubscribe = onSnapshot(qRef, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setIssues(data);
    });
    return () => unsubscribe();
  }, []);

  // ÇÖZÜLEN issues → indeks (plate+title)
  useEffect(() => {
    const qRef = query(collection(db, "issues"), where("resolved", "==", true));
    const unsub = onSnapshot(qRef, (snap) => {
      const idx = {};
      snap.forEach((d) => {
        const v = d.data();
        const targetKey = getTargetKey(v);
        const slug = toSlugTR((v.issue || "").trim());
        if (!targetKey || !slug) return;
        const sec = v?.resolvedAt?.seconds || v?.date?.seconds || null;
        if (!sec) return;
        const key = `${targetKey}|${slug}`;
        if (!idx[key] || sec > idx[key]) {
          idx[key] = sec; // en son
        }
      });
      setLastResolvedIdx(idx);
    });
    return () => unsub();
  }, []);

  // termin koleksiyonu
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "termin"), (snap) => {
      const m = {};
      snap.forEach(d => { m[d.id] = { id: d.id, ...d.data() }; });
      setTerminMap(m);
    });
    return () => unsub();
  }, []);

  // başlık eşlemesi
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "termin_title_map"), (snap) => {
      const m = {};
      snap.forEach(d => { m[d.id] = { id: d.id, ...d.data() }; }); // id=slug
      setTitleMap(m);
    });
    return () => unsub();
  }, []);

  // Bir issue için kategori (termin gün) bul
  const getCategoryForIssue = (it) => {
    if (it.terminCategoryId && terminMap[it.terminCategoryId]) return terminMap[it.terminCategoryId];
    const slug = toSlugTR((it.issue || "").trim());
    const mapDoc = slug ? titleMap[slug] : null;
    if (mapDoc && mapDoc.categoryId && terminMap[mapDoc.categoryId]) {
      return terminMap[mapDoc.categoryId];
    }
    return null;
  };

  // Filtreleme
  useEffect(() => {
    let filtered = issues;

    filtered = filtered.filter(item => selectedTargetTypes.includes(getTargetType(item)));

    if (selectedTesis) {
      filtered = filtered.filter(item => item.tesis === selectedTesis);
    }
    if (selectedPlate) {
      const val = selectedPlate.toLowerCase();
      filtered = filtered.filter(item => getTargetLabel(item).toLowerCase().includes(val));
    }

    if (terminFilter !== "all") {
      filtered = filtered.filter((item) => {
        const cat = getCategoryForIssue(item);
        const terminDays = typeof cat?.days === "number" ? cat.days : null;
        if (terminDays == null) return false;

        const now = new Date();
        const created = item.date?.seconds ? new Date(item.date.seconds * 1000) : null;
        if (!created) return false;

        const passed = Math.ceil((now - created) / (1000 * 60 * 60 * 24));
        const remaining = terminDays - passed;
        const overdue = remaining < 0;

        return terminFilter === "overdue" ? overdue : !overdue;
      });
    }

    setFilteredIssues(filtered);
  }, [issues, selectedTesis, selectedPlate, selectedTargetTypes, terminFilter, terminMap, titleMap]);

  const grouped = useMemo(() => {
    const g = {};
    filteredIssues.forEach((item) => {
      const tesisKey = item.tesis || "Belirtilmemiş";
      if (!g[tesisKey]) g[tesisKey] = {};
      const targetKey = getTargetLabel(item);
      if (!g[tesisKey][targetKey]) g[tesisKey][targetKey] = [];
      g[tesisKey][targetKey].push(item);
    });
    return g;
  }, [filteredIssues]);

  const uniquePlates = [...new Set(issues.map(i => getTargetLabel(i)).filter(Boolean))];

  const exportToExcel = () => {
    const exportData = filteredIssues.map(item => {
      const cat = getCategoryForIssue(item);
      const terminDays = typeof cat?.days === "number" ? cat.days : null;
      let kalan = "-";
      if (terminDays != null && item.date?.seconds) {
        const now = new Date();
        const created = new Date(item.date.seconds * 1000);
        const passed = Math.ceil((now - created) / (1000 * 60 * 60 * 24));
        const remaining = terminDays - passed;
        kalan = remaining > 0 ? `${remaining} gün kaldı` : remaining === 0 ? "bugün" : `${Math.abs(remaining)} gün geçti`;
      }

      return {
        Tesis: item.tesis,
        "Hedef Tipi": getTargetKindLabel(item),
        "Plaka/Santiye/Tesis": getTargetLabel(item),
        Plaka: item.plate || "",
        Santiye: item.siteName || "",
        TesisHedefi: item.facilityName || "",
        SantiyeDurumu: item.siteMatchStatus || "",
        Eksiklik: item.issue,
        Tarih: formatDate(item.date),
        Gun: calculateDaysAgo(item.date),
        "Termin (gün)": terminDays ?? "-",
        "Termine Kalan": kalan,
        Ekleyen: item.createdBy || "-",
        Cözen: item.resolvedBy || "-"
      };
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Eksiklikler");
    XLSX.writeFile(wb, "eksiklikler.xlsx");
  };

  const exportReportToExcel = () => {
    const rows = filteredIssues.map((item, index) => {
      const cat = getCategoryForIssue(item);
      const terminDays = typeof cat?.days === "number" ? cat.days : null;
      let kalan = "-";
      let overdue = false;
      if (terminDays != null && item.date?.seconds) {
        const now = new Date();
        const created = new Date(item.date.seconds * 1000);
        const passed = Math.ceil((now - created) / (1000 * 60 * 60 * 24));
        const remaining = terminDays - passed;
        kalan = remaining > 0 ? `${remaining} gun kaldi` : remaining === 0 ? "bugun" : `${Math.abs(remaining)} gun gecti`;
        overdue = remaining < 0;
      }

      const photos = getPhotoUrls(item).map((url, photoIndex) => `
        <a href="${escapeHtml(url)}">
          <img src="${escapeHtml(url)}" alt="Foto ${photoIndex + 1}" style="width:96px;height:96px;object-fit:cover;border:1px solid #cbd5e1;border-radius:6px;margin:2px;" />
        </a>
      `).join("");

      return `
        <tr>
          <td class="center">${index + 1}</td>
          <td class="tesis">${escapeHtml(item.tesis || "-")}</td>
          <td>
            <div class="target">${escapeHtml(getTargetLabel(item))}</div>
            <div class="muted">${escapeHtml(getTargetKindLabel(item))}</div>
          </td>
          <td>
            <div class="issue">${escapeHtml(item.issue || "-")}</div>
            <div class="muted">Ekleyen: ${escapeHtml(item.createdBy || "-")}</div>
          </td>
          <td>${escapeHtml(formatDate(item.date))} ${escapeHtml(calculateDaysAgo(item.date))}</td>
          <td class="${overdue ? "overdue" : "ok"}">${escapeHtml(kalan)}</td>
          <td class="photos">${photos || "-"}</td>
        </tr>
      `;
    });

    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <style>
            body { font-family: Arial, sans-serif; color: #0f172a; }
            h2 { margin: 0 0 6px 0; text-align: center; }
            .summary { margin: 0 0 14px 0; text-align: center; color: #475569; font-size: 12px; }
            table { border-collapse: collapse; width: 100%; table-layout: fixed; }
            th { background: #1e293b; color: #ffffff; border: 1px solid #94a3b8; padding: 9px; font-size: 12px; }
            td { border: 1px solid #cbd5e1; padding: 8px; vertical-align: top; font-size: 12px; mso-number-format: "\\@"; }
            tr:nth-child(even) td { background: #f8fafc; }
            .center { text-align: center; width: 42px; font-weight: 700; }
            .tesis { font-weight: 700; background: #eef2ff; }
            .target { font-weight: 700; }
            .issue { font-weight: 700; margin-bottom: 4px; }
            .muted { color: #475569; font-size: 11px; margin-top: 3px; }
            .ok { background: #dcfce7; color: #065f46; font-weight: 700; text-align: center; }
            .overdue { background: #fee2e2; color: #991b1b; font-weight: 700; text-align: center; }
            .photos { width: 230px; min-width: 230px; text-align: left; white-space: normal; }
            img { vertical-align: top; }
          </style>
        </head>
        <body>
          <h2>Saha Analiz Raporu</h2>
          <div class="summary">Toplam ${filteredIssues.length} kayit - ${new Date().toLocaleDateString("tr-TR")}</div>
          <table>
            <colgroup>
              <col style="width:42px" />
              <col style="width:120px" />
              <col style="width:170px" />
              <col style="width:260px" />
              <col style="width:110px" />
              <col style="width:125px" />
              <col style="width:230px" />
            </colgroup>
            <thead>
              <tr>
                <th>Sira No</th>
                <th>Tesis</th>
                <th>Plaka/Santiye/Tesis</th>
                <th>Eksiklik</th>
                <th>Tarih</th>
                <th>Termine Kalan</th>
                <th>Fotograf</th>
              </tr>
            </thead>
            <tbody>${rows.join("")}</tbody>
          </table>
        </body>
      </html>
    `;

    downloadHtmlExcel(html, "saha-analiz-raporu.xls");
  };

  // Tesis bazında tüm açık kayıt sayısı (filtre bağımsız)
  const tesisCount = issues.reduce((acc, item) => {
    const key = item.tesis || "Belirtilmemiş";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // Bir kaydın termininin geçip geçmediğini hesapla
  const isOverdue = (item) => {
    const cat = getCategoryForIssue(item);
    const terminDays = typeof cat?.days === "number" ? cat.days : null;
    if (terminDays == null || !item?.date?.seconds) return null;
    const now = new Date();
    const created = new Date(item.date.seconds * 1000);
    const passed = Math.ceil((now - created) / (1000 * 60 * 60 * 24));
    const remaining = terminDays - passed;
    return remaining < 0;
  };

  // Tesis bazında "termini geçen" adet
  const overdueCountByTesis = useMemo(() => {
    const acc = {};
    issues.forEach((it) => {
      const od = isOverdue(it);
      if (od === true) {
        const key = it.tesis || "Belirtilmemiş";
        acc[key] = (acc[key] || 0) + 1;
      }
    });
    return acc;
  }, [issues, terminMap, titleMap]);

  // Yardımcı: kalan süre metni & renklendirme (tablo)
  const calcRemainingInfo = (item) => {
    const cat = getCategoryForIssue(item);
    const terminDays = typeof cat?.days === "number" ? cat.days : null;
    if (terminDays == null || !item?.date?.seconds) return { label: "-", overdue: null };

    const now = new Date();
    const created = new Date(item.date.seconds * 1000);
    const passed = Math.ceil((now - created) / (1000 * 60 * 60 * 24));
    const remaining = terminDays - passed;

    let label = "-";
    if (remaining > 0) label = `${remaining} gün kaldı`;
    else if (remaining === 0) label = "bugün";
    else label = `${Math.abs(remaining)} gün geçti`;

    return { label, overdue: remaining < 0 };
  };

  // 🔥 Yardımcı: aynı plaka + başlık için SON çözümden bu yana kaç gün?
  const getLastResolvedLabel = (item, createdSeconds) => {
    const key = `${getTargetKey(item)}|${toSlugTR((item.issue || "").trim())}`;
    const sec = lastResolvedIdx[key];
    if (!sec) return null;
    // Sadece BU kayıttan önceki çözümleri dikkate al (daha mantıklı)
    if (createdSeconds && sec >= createdSeconds) return null;

    const nowMs = Date.now();
    const days = Math.ceil((nowMs - sec * 1000) / (1000 * 60 * 60 * 24));
    return `Son çözüm: ${days} gün önce`;
  };

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ textAlign: "center", marginBottom: 12 }}>Araç Eksiklikleri</h2>

      {/* Filtreler */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 10,
          marginBottom: 8,
          alignItems: "center",
        }}
      >
        <select value={selectedTesis} onChange={(e) => setSelectedTesis(e.target.value)} style={fieldStyle}>
          <option value="">Tüm Tesisler</option>
          {Object.keys(tesisCount).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        <AutocompletePlate
          value={selectedPlate}
          onCommit={setSelectedPlate}
          options={uniquePlates}
          placeholder="Tum plakalar/santiyeler/tesisler"
        />

        <div style={targetTypeFilterStyle}>
          {targetTypeOptions.map((option) => (
            <label key={option.value} style={targetTypeOptionStyle}>
              <input
                type="checkbox"
                checked={selectedTargetTypes.includes(option.value)}
                onChange={() => toggleTargetType(option.value)}
              />
              {option.label}
            </label>
          ))}
        </div>

        <select value={terminFilter} onChange={(e) => setTerminFilter(e.target.value)} style={fieldStyle}>
          <option value="all">Termin: Tümü</option>
          <option value="not_overdue">Termin: Geçmeyenler</option>
          <option value="overdue">Termin: Geçenler</option>
        </select>

        <button
          onClick={exportReportToExcel}
          style={{ padding: "8px 12px", border: "none", borderRadius: 6, cursor: "pointer", background: "#3b82f6", color: "#fff" }}
        >
          Excel'e Aktar
        </button>
      </div>

      {/* Filtrelenen toplam adet */}
      <div style={{ marginBottom: 10 }}>
        <span style={chipStyle}>Filtrelenen: <b>{filteredIssues.length}</b></span>
      </div>

      {/* Özet KUTULAR */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 10,
          marginBottom: 16,
        }}
      >
        {Object.entries(tesisCount).map(([tesis, count]) => (
          <div
            key={tesis}
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              padding: "10px 12px",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{tesis}: {count}</div>
            <div style={{ fontSize: 12, color: "#475569" }}>
              Termin Geçen: <b>{overdueCountByTesis[tesis] ?? 0}</b>
            </div>
          </div>
        ))}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "850px", fontFamily: "Arial" }}>
          <thead style={{ backgroundColor: "#1e293b", color: "black" }}>
            <tr>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>Tesis</th>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>Plaka/Şantiye/Tesis</th>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>Eksiklik</th>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>Terminin Bitmesine Kalan</th>
              <th style={{ border: "1px solid #ccc", padding: "10px", width: 150 }}>Fotograf</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([tesis, plakaGroup]) => {
              const tesisRowSpan = Object.values(plakaGroup).reduce((acc, items) => acc + items.length, 0);
              let tesisPrinted = false;
              return Object.entries(plakaGroup).map(([plate, itemList]) => {
                return itemList.map((item, itemIdx) => {
                  const { label: kalanLabel, overdue } = calcRemainingInfo(item);

                  const issueCellStyle = {
                    border: "1px solid #ccc",
                    padding: "10px",
                    verticalAlign: "top",
                    backgroundColor: overdue === null ? "transparent" : (overdue ? "#fee2e2" : "#dcfce7"),
                    color: overdue ? "#991b1b" : "#0f172a"
                  };
                  const kalanCellStyle = {
                    border: "1px solid #ccc",
                    padding: "10px",
                    verticalAlign: "top",
                    fontWeight: overdue ? "bold" : "normal",
                    backgroundColor: overdue === null ? "transparent" : (overdue ? "#fee2e2" : "#dcfce7"),
                    color: overdue ? "#991b1b" : "#065f46"
                  };

                  const row = [];

                  if (!tesisPrinted) {
                    row.push(
                      <td key={`tesis-${tesis}`} rowSpan={tesisRowSpan} style={{ border: "1px solid #ccc", padding: "10px", fontWeight: "bold", backgroundColor: "#f1f5f9", verticalAlign: "top" }}>{tesis}</td>
                    );
                    tesisPrinted = true;
                  }

                  if (itemIdx === 0) {
                    row.push(
                      <td key={`plate-${plate}`} rowSpan={itemList.length} style={{ border: "1px solid #ccc", padding: "10px", fontWeight: "bold", backgroundColor: "#e2e8f0", verticalAlign: "top" }}>
                        <div>{plate}</div>
                        <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>{getTargetKindLabel(item)}</div>
                      </td>
                    );
                  }

                  // 🔥 Yeni satır: Son çözüm bilgisi (varsa)
                  const createdSec = item?.date?.seconds || null;
                  const lastResolvedLabel = getLastResolvedLabel(item, createdSec);
                  const isUnlinkedSite = getTargetType(item) === "site" && item.siteMatchStatus !== "linked";
                  const photoUrls = getPhotoUrls(item);

                  row.push(
                    <td key={`issue-${item.id}`} style={issueCellStyle}>
                      <div style={{ fontWeight: "bold" }}>
                        {item.issue} <span style={{ fontSize: "0.8em", color: "#666" }}>{calculateDaysAgo(item.date)}</span>
                      </div>

                      {lastResolvedLabel && (
                        <div style={{ fontSize: "0.8em", color: "#475569", marginTop: 4 }}>
                          {lastResolvedLabel}
                        </div>
                      )}

                      {isUnlinkedSite && (
                        <div style={{ fontSize: "0.8em", color: "#b45309", marginTop: 4 }}>
                          Baglanmamis santiye
                        </div>
                      )}

                      <div style={{ fontSize: "0.75em", color: "#555", marginTop: 2 }}>
                        Ekleyen: {item.createdBy || "-"}
                      </div>

                      {item.resolvedBy && (
                        <div style={{ fontSize: "0.75em", color: "green" }}>Çözen: {item.resolvedBy}</div>
                      )}
                    </td>
                  );

                  row.push(
                    <td key={`kalan-${item.id}`} style={kalanCellStyle}>
                      {kalanLabel}
                    </td>
                  );

                  row.push(
                    <td key={`photos-${item.id}`} style={photoCellStyle}>
                      {photoUrls.length > 0 ? (
                        <div style={photoWrapStyle}>
                          {photoUrls.map((url, idx) => (
                            <a
                              key={`${url}-${idx}`}
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              title={`Foto ${idx + 1}`}
                              style={photoLinkStyle}
                            >
                              <img
                                src={url}
                                alt={`Foto ${idx + 1}`}
                                loading="lazy"
                                style={photoImgStyle}
                                onError={(event) => {
                                  event.currentTarget.style.display = "none";
                                }}
                              />
                              <span style={{ position: "absolute", inset: "auto 0 0 0", background: "rgba(15,23,42,0.72)", color: "#fff", fontSize: 10, lineHeight: "14px", textAlign: "center" }}>
                                {idx + 1}
                              </span>
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: "#94a3b8", fontSize: 12 }}>-</span>
                      )}
                    </td>
                  );

                  return <tr key={item.id}>{row}</tr>;
                });
              });
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ViewIssues;
